import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Funnel, ArrowUpDown } from "lucide-react";
import {
  getPendingUserRequestsApi,
  resendAdminOtpApi,
  resendUserInviteApi,
} from "../../api/users/userApi";
import { useUser } from "../../context/UserContext";
import { toast } from "react-toastify";
import { canManageOrgUsers } from "../../utils/roles";

interface PendingRequest {
  request_id: string;
  target_phone: string;
  target_email: string | null;
  target_user_name: string | null;
  status: "pending_admin_verification" | "pending_user_approval" | "approved" | "rejected";
  created_at: string;
  expires_at: string | null;
  admin_verified: boolean;
  user_approved: boolean | null;
}

interface PendingRequestsProps {
  onBack: () => void;
  onVerify: (requestId: string) => void;
  refreshKey: number;
}

const PAGE_SIZE = 20;
const DEBOUNCE_DELAY = 500;

const PendingRequests: React.FC<PendingRequestsProps> = ({
  onBack,
  onVerify,
  refreshKey,
}) => {
  const { user } = useUser();

  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [debouncedFilters, setDebouncedFilters] = useState<{ [key: string]: string }>({});
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [filterPosition, setFilterPosition] = useState<{ top: number; left: number } | null>(null);
  const [showGlobalSort, setShowGlobalSort] = useState(false);

  const filterTimerRef = useRef<NodeJS.Timeout | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, DEBOUNCE_DELAY);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [filters]);

  const sortQuery = useMemo(() => {
    if (!sortField) return undefined;
    return sortOrder === "desc" ? `-${sortField}` : sortField;
  }, [sortField, sortOrder]);

  const filterQuery = useMemo(() => {
    const filterConditions: string[] = [];
    Object.entries(debouncedFilters).forEach(([field, value]) => {
      if (value && value.trim() !== "") {
        if (field === 'status') {
          filterConditions.push(`${field}=${value}`);
        } else {
          filterConditions.push(`${field}~${value}`);
        }
      }
    });
    return filterConditions.length === 0 ? undefined : filterConditions.join(' AND ');
  }, [debouncedFilters]);

  useEffect(() => {
    if (!user?.org_id) return;

    const fetchPending = async () => {
      try {
        setLoading(true);
        const res = await getPendingUserRequestsApi({
          tenantId: String(user.org_id),
          page,
          page_size: PAGE_SIZE,
          sort: sortQuery,
          filter: filterQuery,
        });
        setRequests(res.requests || []);
        setTotal(res.total || 0);
      } catch (error: any) {
        console.error("API Error:", error);
        toast.error("Failed to fetch pending requests");
      } finally {
        setLoading(false);
      }
    };

    fetchPending();
  }, [user?.org_id, refreshKey, page, sortQuery, filterQuery]);

  const handleSortSelect = useCallback((field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
    setPage(1);
    setShowGlobalSort(false);
  }, []);

  const clearSort = useCallback(() => {
    setSortField(null);
    setSortOrder("asc");
    setPage(1);
    setShowGlobalSort(false);
  }, []);

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterClear = useCallback((field: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setDebouncedFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setPage(1);
  }, []);

  const handleDropdownSelect = useCallback((field: string, value: string) => {
    if (value === '') {
      handleFilterClear(field);
    } else {
      setFilters((prev) => ({ ...prev, [field]: value }));
      setDebouncedFilters((prev) => ({ ...prev, [field]: value }));
      setPage(1);
    }
    setActiveFilterColumn(null);
    setFilterPosition(null);
  }, [handleFilterClear]);

  const toggleFilterDropdown = useCallback((field: string, event: React.MouseEvent) => {
    if (activeFilterColumn === field) {
      setActiveFilterColumn(null);
      setFilterPosition(null);
    } else {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setFilterPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
      setActiveFilterColumn(field);
    }
    setShowGlobalSort(false);
  }, [activeFilterColumn]);

  const handleResendAdminOtp = async (requestId: string) => {
    if (!user?.org_id) return;
    try {
      await resendAdminOtpApi({ tenantId: String(user.org_id), requestId });
      toast.success("OTP resent to admin");
      onVerify(requestId);
    } catch (error) {
      toast.error("Failed to resend OTP");
    }
  };

  const handleResendUserInvite = async (requestId: string) => {
    if (!user?.org_id) return;
    try {
      await resendUserInviteApi({ tenantId: String(user.org_id), requestId });
      toast.success("Invitation resent to user");
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const renderAction = (r: PendingRequest) => {
    if (!canManageOrgUsers()) return "--";
    if (r.status === "pending_admin_verification") {
      return (
        <button className="primary-btn small" onClick={() => handleResendAdminOtp(r.request_id)}>
          Verify
        </button>
      );
    }
    if (r.status === "pending_user_approval") {
      return (
        <button className="secondary-btn small" onClick={() => handleResendUserInvite(r.request_id)}>
          Resend
        </button>
      );
    }
    return "--";
  };

  const FilterIcon = ({ field }: { field: string }) => {
    const isActive = filters[field] && filters[field].trim() !== "";
    return (
      <span 
        className={`filter-icon ${isActive ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleFilterDropdown(field, e);
        }}
      >
        <Funnel size={14} />
      </span>
    );
  };

  const FilterDropdown = ({ field }: { field: string }) => {
    if (activeFilterColumn !== field || !filterPosition) return null;

    const dropdownStyle: React.CSSProperties = {
      position: 'fixed',
      top: `${filterPosition.top}px`,
      left: `${filterPosition.left}px`,
      zIndex: 10000,
    };

    if (field === 'status') {
      const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'pending_admin_verification', label: 'Pending Admin Verification' },
        { value: 'pending_user_approval', label: 'Pending User Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
      ];

      return (
        <div className="filter-dropdown" style={dropdownStyle} onClick={(e) => e.stopPropagation()}>
          <div className="filter-dropdown-content">
            {statusOptions.map((option) => (
              <div
                key={option.value}
                className={`filter-option ${filters[field] === option.value ? 'selected' : ''}`}
                onClick={() => handleDropdownSelect(field, option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="filter-dropdown" style={dropdownStyle} onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          placeholder={`Filter ${field}...`}
          value={filters[field] || ""}
          onChange={(e) => handleFilterChange(field, e.target.value)}
          autoFocus
        />
        {filters[field] && (
          <button className="clear-filter-btn" onClick={() => handleFilterClear(field)}>
            Clear
          </button>
        )}
      </div>
    );
  };

  const GlobalSortDropdown = () => {
    if (!showGlobalSort) return null;

    const sortOptions = [
      { field: 'created_at', order: 'desc' as const, label: 'Newest First' },
      { field: 'created_at', order: 'asc' as const, label: 'Oldest First' },
    ];

    return (
      <div className="global-sort-dropdown" onClick={(e) => e.stopPropagation()}>
        <div className="global-sort-content">
          {sortOptions.map((option, idx) => (
            <div
              key={idx}
              className={`sort-option ${sortField === option.field && sortOrder === option.order ? 'selected' : ''}`}
              onClick={() => handleSortSelect(option.field, option.order)}
            >
              {option.label}
            </div>
          ))}
          {sortField && (
            <div className="sort-option clear" onClick={clearSort}>Clear Sort</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pending-requests" onClick={() => { setActiveFilterColumn(null); setFilterPosition(null); }}>
      <div className="pending-requests__header">
        <h2>Pending requests</h2>
        <div className="header-actions">
          <button 
            className={`icon-btn ${sortField ? 'active' : ''}`}
            onClick={() => { setShowGlobalSort(!showGlobalSort); setActiveFilterColumn(null); }}
            title="Sort"
          >
            <ArrowUpDown size={18} />
          </button>
          <GlobalSortDropdown />
          <button className="secondary-btn" onClick={onBack}>Back</button>
        </div>
      </div>

      {loading ? (
        <div className="loader-container"><div className="loader" /></div>
      ) : (
        <>
          <div className="pending-requests__table-wrapper">
            <table className="pending-requests__table">
              <thead>
                <tr>
                  <th>
                    <div className="th-content">
                      <span className="th-label">Phone</span>
                      <FilterIcon field="target_phone" />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label">Email</span>
                      <FilterIcon field="target_email" />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label">Name</span>
                      <FilterIcon field="target_user_name" />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label">Status</span>
                      <FilterIcon field="status" />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span className="th-label">Requested on</span>
                      <FilterIcon field="created_at" />
                    </div>
                  </th>
                  <th>Message</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.request_id}>
                    <td>{r.target_phone}</td>
                    <td>{r.target_email || "-"}</td>
                    <td>{r.target_user_name || "-"}</td>
                    <td>
                      <span className="status pending">
                        {r.status === "pending_admin_verification" ? "Admin verification pending"
                          : r.status === "pending_user_approval" ? "Waiting for user approval"
                          : r.status === "approved" ? "Approved" : "Rejected"}
                      </span>
                    </td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      {r.status === "pending_admin_verification" ? "Admin verification required"
                        : r.status === "pending_user_approval" ? "Waiting for user approval"
                        : r.status === "approved" ? "Request approved" : "Request rejected"}
                    </td>
                    <td>{renderAction(r)}</td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: "center" }}>No pending requests</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <FilterDropdown field={activeFilterColumn || ''} />
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PendingRequests;